import React, { useEffect, useRef, useState } from 'react';
import { apiGet, apiPut, apiPatch, apiPost, apiDelete, authedFetch, API_BASE } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Megaphone, Upload, Trash2, ArrowUp, ArrowDown, Eye, EyeOff, ImagePlus, Info, Timer, BarChart3, MousePointerClick, CalendarClock, RotateCcw } from 'lucide-react';

const STATUS_STYLES = {
  live: { cls: 'bg-emerald-500', label: 'Live' },
  scheduled: { cls: 'bg-amber-500', label: 'Scheduled' },
  expired: { cls: 'bg-slate-500', label: 'Expired' },
  hidden: { cls: 'bg-slate-400', label: 'Hidden' },
};

// image_url comes back as "/api/website-ads/{id}/image"; API_BASE already ends in /api
const adSrc = (imageUrl) => `${API_BASE}${imageUrl.slice(4)}`;

export default function WebsiteAdsManager() {
  const [ads, setAds] = useState([]);
  const [maxAds, setMaxAds] = useState(5);
  const [rotation, setRotation] = useState(1);
  const [minRot, setMinRot] = useState(1);
  const [maxRot, setMaxRot] = useState(10);
  const [savingRot, setSavingRot] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const rotationTouched = useRef(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await apiGet('/admin/website-ads');
      setAds(d.ads || []);
      setMaxAds(d.max_ads || 5);
      setMinRot(d.min_rotation_seconds || 1);
      setMaxRot(d.max_rotation_seconds || 10);
      // Don't clobber a value the admin just changed while this load was in flight.
      if (!rotationTouched.current) setRotation(Number(d.rotation_seconds) || 1);
    } catch {
      toast.error('Could not load ads');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const saveRotation = async (secs) => {
    rotationTouched.current = true;
    setRotation(secs);
    setSavingRot(true);
    try {
      await apiPut('/admin/website-ads/settings', { rotation_seconds: secs });
      toast.success(`Ads now rotate every ${secs} second${secs === 1 ? '' : 's'}`);
    } catch { toast.error('Could not save rotation speed'); }
    setSavingRot(false);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.(jpe?g|png)$/i.test(file.name)) {
      toast.error('Only .jpeg or .png files are allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large — max 5 MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await authedFetch(`${API_BASE}/admin/website-ads`, { method: 'POST', body: fd });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Upload failed (${res.status})`);
      }
      toast.success('Ad added — it is now live on the landing page');
      await load();
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const toggleActive = async (ad) => {
    try {
      const updated = await apiPatch(`/admin/website-ads/${ad.id}`, { active: !ad.active });
      setAds(prev => prev.map(a => a.id === ad.id ? { ...a, ...updated } : a));
    } catch { toast.error('Update failed'); }
  };

  const saveField = async (ad, field, value) => {
    try {
      const updated = await apiPatch(`/admin/website-ads/${ad.id}`, { [field]: value });
      setAds(prev => prev.map(a => a.id === ad.id ? { ...a, ...updated } : a));
    } catch { toast.error('Update failed'); }
  };

  // datetime-local (local wall-clock) <-> stored ISO (UTC)
  const isoToInput = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d)) return '';
    const p = (x) => String(x).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  };
  const saveDate = async (ad, field, inputVal) => {
    const iso = inputVal ? new Date(inputVal).toISOString() : '';
    // guard against an end date before the start date
    if (field === 'end_at' && iso && ad.start_at && new Date(iso) <= new Date(ad.start_at)) {
      toast.error('End time must be after the start time');
      return;
    }
    if (field === 'start_at' && iso && ad.end_at && new Date(iso) >= new Date(ad.end_at)) {
      toast.error('Start time must be before the end time');
      return;
    }
    try {
      const updated = await apiPatch(`/admin/website-ads/${ad.id}`, { [field]: iso });
      setAds(prev => prev.map(a => a.id === ad.id ? { ...a, ...updated } : a));
      toast.success(inputVal ? 'Schedule updated' : 'Date cleared');
    } catch { toast.error('Could not save schedule'); }
  };
  const resetStats = async (ad) => {
    if (!window.confirm('Reset impressions and clicks for this ad to zero?')) return;
    try {
      const updated = await apiPost(`/admin/website-ads/${ad.id}/reset-stats`, {});
      setAds(prev => prev.map(a => a.id === ad.id ? { ...a, ...updated } : a));
      toast.success('Stats reset');
    } catch { toast.error('Reset failed'); }
  };

  const remove = async (ad) => {
    if (!window.confirm('Delete this ad? It will stop showing on the website immediately.')) return;
    try {
      await apiDelete(`/admin/website-ads/${ad.id}`);
      toast.success('Ad deleted');
      setAds(prev => prev.filter(a => a.id !== ad.id));
    } catch { toast.error('Delete failed'); }
  };

  const move = async (idx, dir) => {
    const next = [...ads];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setAds(next);
    try {
      await fetchReorder(next.map(a => a.id));
    } catch { toast.error('Reorder failed'); load(); }
  };

  const fetchReorder = async (orderedIds) => {
    const res = await authedFetch(`${API_BASE}/admin/website-ads/reorder`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ordered_ids: orderedIds }),
    });
    if (!res.ok) throw new Error('reorder failed');
  };

  const activeCount = ads.filter(a => a.active).length;
  const canAdd = ads.length < maxAds;

  return (
    <div className="space-y-6" data-testid="website-ads-manager">
      <Card className="p-6 rounded-2xl border-slate-200">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center text-white">
              <Megaphone className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-heading text-lg font-bold text-slate-900">Website Ads</h2>
              <p className="text-xs text-slate-500 font-body">
                Posters shown in the rotating carousel at the top of the landing page. Changes go live instantly.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="rounded-full" data-testid="ads-count-badge">
              {ads.length} / {maxAds} uploaded · {activeCount} live
            </Badge>
            <input
              ref={fileRef}
              type="file"
              accept=".jpg,.jpeg,.png,image/jpeg,image/png"
              className="hidden"
              onChange={handleUpload}
              data-testid="ad-file-input"
            />
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={!canAdd || uploading}
              className="rounded-full bg-slate-900 hover:bg-slate-800"
              data-testid="add-ad-btn"
            >
              <Upload className="w-4 h-4 mr-1.5" />
              {uploading ? 'Uploading…' : 'Add Ad'}
            </Button>
          </div>
        </div>

        <div className="mt-4 flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-xl px-3.5 py-2.5">
          <Info className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-0.5" />
          <span>
            Upload <strong>2 to {maxAds}</strong> poster images (.jpeg / .png, max 5&nbsp;MB each). Portrait posters (e.g. 1024×1536) look best.
            {ads.length < 2 && ' Add at least 2 for the carousel to rotate.'}
          </span>
        </div>
      </Card>

      {/* Rotation speed control */}
      <Card className="p-5 rounded-2xl border-slate-200" data-testid="ads-rotation-card">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
          <div className="flex items-center gap-3 min-w-[220px]">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600">
              <Timer className="w-5 h-5" />
            </div>
            <div>
              <p className="font-heading text-sm font-bold text-slate-900">Rotation speed</p>
              <p className="text-xs text-slate-500">How long each ad stays visible</p>
            </div>
          </div>
          <div className="flex-1 flex items-center gap-4">
            <input
              type="range"
              min={minRot}
              max={maxRot}
              step={1}
              value={rotation}
              onChange={(e) => setRotation(Number(e.target.value))}
              onMouseUp={(e) => saveRotation(Number(e.target.value))}
              onTouchEnd={(e) => saveRotation(Number(e.target.value))}
              onKeyUp={(e) => saveRotation(Number(e.target.value))}
              className="flex-1 accent-indigo-600 cursor-pointer"
              data-testid="ads-rotation-slider"
            />
            <div className="w-24 text-right">
              <span className="font-display text-2xl font-bold text-slate-900" data-testid="ads-rotation-value">{rotation}</span>
              <span className="text-xs text-slate-500 ml-1">sec{rotation === 1 ? '' : 's'}</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {[1, 2, 5, 10].map(s => (
              <Button
                key={s}
                size="sm"
                variant={rotation === s ? 'default' : 'outline'}
                className="rounded-full h-8 px-3 text-xs"
                onClick={() => saveRotation(s)}
                data-testid={`ads-rotation-preset-${s}`}
              >
                {s}s
              </Button>
            ))}
          </div>
        </div>
        <p className="text-[11px] text-slate-500 mt-3">
          {savingRot ? 'Saving…' : `Live now: each ad shows for ${rotation} second${rotation === 1 ? '' : 's'} before sliding to the next. Hovering pauses it.`}
        </p>
      </Card>

      {loading ? (
        <p className="text-sm text-slate-400 font-body px-1">Loading ads…</p>
      ) : ads.length === 0 ? (
        <Card className="p-10 rounded-2xl border-dashed border-2 border-slate-200 text-center" data-testid="ads-empty">
          <ImagePlus className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="font-heading text-sm font-semibold text-slate-700">No ads yet</p>
          <p className="text-xs text-slate-500 mt-1">Click “Add Ad” to upload your first poster.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="ads-grid">
          {ads.map((ad, idx) => (
            <Card key={ad.id} className="overflow-hidden rounded-2xl border-slate-200" data-testid={`ad-card-${ad.id}`}>
              <div className="relative bg-slate-100 aspect-[3/4]">
                <img
                  src={adSrc(ad.image_url)}
                  alt={ad.alt_text || 'Ad preview'}
                  className="w-full h-full object-cover"
                  data-testid={`ad-preview-${ad.id}`}
                />
                <div className="absolute top-2 left-2 flex items-center gap-1.5">
                  <Badge className={`rounded-full text-[10px] ${(STATUS_STYLES[ad.schedule_status] || STATUS_STYLES.hidden).cls}`} data-testid={`ad-status-${ad.id}`}>
                    {(STATUS_STYLES[ad.schedule_status] || STATUS_STYLES.hidden).label}
                  </Badge>
                  <Badge variant="secondary" className="rounded-full text-[10px]">#{idx + 1}</Badge>
                </div>
              </div>
              <div className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Switch checked={ad.active} onCheckedChange={() => toggleActive(ad)} data-testid={`ad-toggle-${ad.id}`} />
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      {ad.active ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                      {ad.active ? 'Showing' : 'Hidden'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" disabled={idx === 0} onClick={() => move(idx, -1)} data-testid={`ad-up-${ad.id}`}>
                      <ArrowUp className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" disabled={idx === ads.length - 1} onClick={() => move(idx, 1)} data-testid={`ad-down-${ad.id}`}>
                      <ArrowDown className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-500 hover:text-rose-600 hover:bg-rose-50" onClick={() => remove(ad)} data-testid={`ad-delete-${ad.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <Input
                  defaultValue={ad.alt_text}
                  placeholder="Alt text (accessibility / SEO)"
                  className="text-xs h-8"
                  onBlur={(e) => e.target.value !== ad.alt_text && saveField(ad, 'alt_text', e.target.value)}
                  data-testid={`ad-alt-${ad.id}`}
                />
                <Input
                  defaultValue={ad.link_url}
                  placeholder="Click-through link (optional)"
                  className="text-xs h-8"
                  onBlur={(e) => e.target.value !== ad.link_url && saveField(ad, 'link_url', e.target.value)}
                  data-testid={`ad-link-${ad.id}`}
                />

                {/* Performance */}
                <div className="grid grid-cols-3 gap-2 pt-1" data-testid={`ad-stats-${ad.id}`}>
                  <div className="rounded-lg bg-slate-50 border border-slate-100 p-2 text-center">
                    <div className="flex items-center justify-center gap-1 text-slate-500"><BarChart3 className="w-3 h-3" /><span className="text-[10px] uppercase tracking-wide">Views</span></div>
                    <p className="font-heading text-sm font-bold text-slate-800" data-testid={`ad-impressions-${ad.id}`}>{ad.impressions ?? 0}</p>
                  </div>
                  <div className="rounded-lg bg-slate-50 border border-slate-100 p-2 text-center">
                    <div className="flex items-center justify-center gap-1 text-slate-500"><MousePointerClick className="w-3 h-3" /><span className="text-[10px] uppercase tracking-wide">Clicks</span></div>
                    <p className="font-heading text-sm font-bold text-slate-800" data-testid={`ad-clicks-${ad.id}`}>{ad.clicks ?? 0}</p>
                  </div>
                  <div className="rounded-lg bg-indigo-50 border border-indigo-100 p-2 text-center">
                    <div className="flex items-center justify-center gap-1 text-indigo-500"><span className="text-[10px] uppercase tracking-wide">CTR</span></div>
                    <p className="font-heading text-sm font-bold text-indigo-700" data-testid={`ad-ctr-${ad.id}`}>{(ad.ctr ?? 0)}%</p>
                  </div>
                </div>
                <button
                  onClick={() => resetStats(ad)}
                  className="text-[10px] text-slate-400 hover:text-slate-600 inline-flex items-center gap-1"
                  data-testid={`ad-reset-stats-${ad.id}`}
                >
                  <RotateCcw className="w-3 h-3" /> Reset stats
                </button>

                {/* Schedule (optional auto show/hide) */}
                <div className="pt-2 border-t border-slate-100">
                  <p className="flex items-center gap-1.5 text-[11px] font-heading font-semibold text-slate-600 mb-1.5">
                    <CalendarClock className="w-3.5 h-3.5 text-slate-400" /> Schedule (optional)
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-slate-500 uppercase tracking-wide">Starts</label>
                      <Input
                        type="datetime-local"
                        defaultValue={isoToInput(ad.start_at)}
                        className="text-[11px] h-8"
                        onBlur={(e) => { const v = e.target.value; const cur = isoToInput(ad.start_at); if (v !== cur) saveDate(ad, 'start_at', v); }}
                        data-testid={`ad-start-${ad.id}`}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-500 uppercase tracking-wide">Ends</label>
                      <Input
                        type="datetime-local"
                        defaultValue={isoToInput(ad.end_at)}
                        className="text-[11px] h-8"
                        onBlur={(e) => { const v = e.target.value; const cur = isoToInput(ad.end_at); if (v !== cur) saveDate(ad, 'end_at', v); }}
                        data-testid={`ad-end-${ad.id}`}
                      />
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1.5">Leave blank to run always. Times are in your local timezone; the ad auto-shows/hides accordingly.</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
